// THE EVIDENCE A RAW TASK CARRIES — every bound the stored ask is held to, the URL
// predicate, and the anchor / evidence / origin-ref shapes those bounds apply to.
// Split out of `../raw-task.ts`, which is now the barrel over this directory.

import { z } from 'zod';

/** Anchors per raw task. A plan cites places, it does not inventory the repo. */
export const RAW_TASK_ANCHORS_MAX = 100;
/** Same bound as `FILES_TOUCHED_PATH_MAX` — an anchor path is a repo path. */
export const RAW_TASK_ANCHOR_PATH_MAX = 300;
export const RAW_TASK_URLS_MAX = 20;
/**
 * Paths one raw task says it expects to WRITE (#5287), and the note on one. The count is
 * `FILES_TOUCHED_MAX`'s: the hint narrows a piece's lock and a lock holds at most that many.
 */
export const RAW_TASK_WRITES_MAX = 100;
export const RAW_TASK_WRITE_WHY_MAX = 200;
export const RAW_TASK_URL_MAX = 500;
/**
 * Excerpts per raw task, and the length of one. Both are DELIBERATELY tight:
 * they are what makes "`path:line` anchors and urls, never pasted bodies" a
 * property of the schema. A Sentry stack frame or one line of a ticket fits; an
 * issue body does not, and the parse refuses it at the inlet instead of storing
 * a copy of somebody's text that then has to be redacted, retained and reasoned
 * about. The body is already durable in `inbound_events` — this table holds
 * POINTERS at it.
 */
export const RAW_TASK_EXCERPTS_MAX = 10;
export const RAW_TASK_EXCERPT_MAX = 280;
/**
 * The health metric a filed finding says the work should move (#5376) — one line
 * naming the number, never a report: the counts behind it ride in `excerpts`.
 */
export const RAW_TASK_METRIC_MAX = 200;
/** Access requirements the scout named — keys, MCP servers, secrets. */
export const RAW_TASK_REQUIRED_ACCESS_MAX = 20;
export const RAW_TASK_REQUIRED_ACCESS_ITEM_MAX = 120;
/** Repos one raw task may span. Plural by design; still bounded. */
export const RAW_TASK_REPOS_MAX = 20;
/**
 * Existing tasks one ask may be ordered behind (`raw_tasks.after_task_ids`). Every
 * id is copied onto EVERY root task the ask's plan mints and re-read by the claim
 * gate on every pass, so the list is fan-out on the hottest query the queue runs;
 * an ask that must wait on more than this many separate pieces of work is waiting
 * on a plan, and the honest shape is to name that plan's LAST tasks.
 */
export const RAW_TASK_AFTER_TASKS_MAX = 20;
/**
 * Executable tasks ONE raw task may fan out into — the cap on
 * {@link RawTaskVerdictSchema}'s `task_ids`.
 *
 * It is NOT {@link RAW_TASK_REPOS_MAX}, which is what it used to be, and the two
 * count different things: repos are how wide the ask reaches, tasks are how many
 * pieces triage cut it into. A twenty-repo ask decomposed two ways per repo is a
 * legitimate forty-task plan that the repo cap would have refused — a bound
 * shaping legitimate output instead of fencing a malformed one, which is the
 * defect {@link RAW_TASK_ASK_MAX} spells out at length.
 *
 * DERIVED, like that one, so it sits ABOVE anything a valid plan composes to:
 * `RAW_TASK_REPOS_MAX` repos x 12 slices each. Twelve is the fleet's own
 * per-plan group ceiling (`SCOUT_MAX_GROUPS`, `apps/runner`), restated rather
 * than imported because the dependency runs the other way — `@developerz/domain`
 * imports nothing internal. If that ceiling moves, this moves with it.
 */
export const RAW_TASK_TASK_IDS_MAX = RAW_TASK_REPOS_MAX * 12;
/**
 * The ask, in prose. DERIVED from the envelope caps it is composed out of, not
 * chosen — and that is the difference between a bound and a truncation.
 *
 * `rawTaskFromEnvelope` composes `title` + `problem` + the acceptance criteria.
 * A VALID envelope maxes out at 200 (title) + 10,000 (problem) + 20 × 500
 * (criteria) plus their separators and bullets — about 20,300 characters. A cap
 * below that would mean one of two things at the front door, and both are the
 * defect this arc already spent slice #2310 on: silently shortening what a human
 * wrote, or refusing an ask the scout was entitled to make.
 *
 * So the cap sits ABOVE what any valid envelope can compose to. It is a
 * fail-fast gate against a hand-built draft, never a shaper of legitimate input,
 * and `raw-normalize.test.ts` composes a maximum envelope through the projection
 * to prove the headroom is real — so a future widening of `problem` or the
 * criteria caps turns this into a red test rather than a silent truncation.
 */
export const RAW_TASK_ASK_MAX = 24_000;
/**
 * How long one claim of a raw task is exclusive for.
 *
 * A claim moves the row out of `pending`, so without an expiry the exclusion is
 * PERMANENT: a pass that dies between the claim and the settle leaves the row
 * `triaged` with a null verdict and nothing ever looks at it again. That is a
 * silent leak of work an inlet was told had been admitted, and it is a shape
 * defect rather than a bug — the column has to exist for the recovery to be
 * possible at all, which is why it lands with the table instead of after it.
 *
 * FIFTEEN MINUTES, and the number is a floor derived from the work it covers, not
 * a guess. This slice's pass is a single UPDATE; slice 02's is a triage session —
 * a real model call with tools, on the account's own key, which is seconds to
 * minutes and occasionally much worse on a slow provider. A lease shorter than
 * one worst-case pass is the dangerous direction: it lets a second pass judge an
 * ask the first is still judging, and the two then disagree in the trail. A lease
 * that is too long only delays recovery of a genuinely dead pass.
 *
 * WHAT IT HAS TO COVER IS ONE JUDGEMENT, NOT A PASS (deep sweep 07-34). A pass can
 * judge more than once (the corrective re-ask, the L9b re-judge), so the triage lane
 * RENEWS the claim before every judge call (`renewRawTaskLeases`, `apps/agents-manager`
 * `triage-claim-renewal.ts`) instead of this constant growing to a sum the next judge
 * call breaks. It must outlast the screens before the first renewal and ONE judge
 * call after each (`@developerz/agent` `screen/screen.test.ts` pins both). A judge
 * deadline that outgrows it moves this constant with it.
 */
export const RAW_TASK_LEASE_MS = 15 * 60_000;

/**
 * How many CLAIMS one ask gets before the triage lane stops trying to judge it and
 * settles it as an escalation instead (#3352).
 *
 * WHY A BOUND EXISTS AT ALL. {@link RAW_TASK_LEASE_MS} makes a stranded claim
 * recoverable — the lapse returns the row to the claimable set — and that recovery
 * is unconditional, so a fault that recurs on every pass is handed the same ask
 * forever. Measured on prod 2026-09-07: `rtk_6d6a5da1ff424bf7bc72d3a2659e9082`
 * claimed first at 2026-09-02 18:05 and still unsettled four days later, roughly
 * 380 redeliveries, the agents-manager naming `{"kind":"skipped","reason":
 * "no_byok"}` on each. The lane was right to decline — a tenant with no working
 * key cannot be judged and guessing would be worse — but a decline that settles
 * nothing is indistinguishable from nothing happening, and the person who filed
 * that issue was told nothing for four days.
 *
 * FIVE, and the number is derived rather than picked. The drain ticks every 5
 * minutes and the lease is 15, so five claims span roughly 75 minutes. Every
 * TRANSIENT fault this lane meets is shorter than that by construction — a worker
 * restart, a NATS blip, one bad model answer, a key that recovers on the provider's
 * own reset — and the two faults that are NOT transient (`no_account`, and a key
 * the tenant has not replaced) do not clear at any number of attempts. So the bound
 * separates the two populations at the first point where waiting has stopped
 * being evidence, and the ask gets a terminal answer roughly an hour after it stopped
 * being decidable rather than never.
 *
 * THAT ANSWER IS NOT A HUMAN'S (2026-10-22 owner ruling: no human works the escalated
 * queue). `attempts_exhausted` is a machine failure (`RAW_TASK_MACHINE_FAILURE_REASONS`,
 * `./redrive`), so the worker's machine re-drive returns the ask to `pending` with a fresh
 * ladder after a backoff, at most `RAW_TASK_MACHINE_REDRIVE_BUDGET` times; only past that
 * does the escalation stand. This bound and that budget multiply: a never-decidable ask
 * spends at most `(1 + budget) × MAX_ATTEMPTS` claims.
 *
 * IT IS NOT A COST CAP. The cost of a redelivery is a claim UPDATE and, once the
 * tenant has a key again, one share of a batched judgement; the bound exists
 * because an ask with no terminal answer is a promise silently broken, not because
 * of the spend. See `docs/standards/agent-budgets.md` for the distinction.
 */
export const RAW_TASK_MAX_ATTEMPTS = 5;

/**
 * Has this ask been claimed more times than {@link RAW_TASK_MAX_ATTEMPTS} allows?
 *
 * Takes the count the CLAIM stamped, so the reading is "this pass is attempt N".
 * Strictly greater, so an ask is judged on its fifth claim and settled on its
 * sixth: the bound is how many chances it gets, not how many it gets minus one.
 */
export function rawTaskAttemptsExhausted(attempts: number): boolean {
  return attempts > RAW_TASK_MAX_ATTEMPTS;
}

/**
 * Is this claim the LAST one {@link RAW_TASK_MAX_ATTEMPTS} allows? Takes the count
 * the CLAIM stamped, like {@link rawTaskAttemptsExhausted}, and reads it one claim
 * ahead: true when the NEXT claim would be settled `attempts_exhausted`. A
 * `judge_transient` deferral on this claim is held `model_unavailable` instead of
 * re-entering the bound (#5114, D1 of #5099) — a transient judge that outlives the
 * attempt bound is model unavailability too, never a terminal verdict.
 */
export function rawTaskOnLastAttempt(attempts: number): boolean {
  return rawTaskAttemptsExhausted(attempts + 1);
}
/** `(account_id, dedupe_key)` is unique — this is the length of the key half. */
export const RAW_TASK_DEDUPE_KEY_MAX = 200;

/**
 * A followable link. `evidence.urls` is the field a human or an agent CLICKS, so
 * "any non-empty string under 500 chars" was not a contract — it admitted
 * `http://` (no host at all), a bare id, and a `javascript:` scheme alike.
 *
 * Parsed with the platform's own URL parser rather than a regex, and restricted
 * to `http`/`https`: the WHATWG parser is the thing that will eventually resolve
 * the link, so agreeing with it is the only check that means anything, and every
 * other scheme is either unfollowable here or actively unsafe to hand onward.
 * Validated at the SCHEMA — the boundary the value enters through — so no
 * producer has to remember to.
 *
 * EXPORTED because the two CALLER-facing doors onto `evidence.urls` — the MCP
 * `ask file` verb and its REST twin `POST /v1/asks` — each declare their own
 * input schema, and each had retyped this field as `min(1).max(RAW_TASK_URL_MAX)`
 * while their prose promised http(s). That is not a cosmetic drift: `createRawTask`
 * INSERTS and then parses the row it reads back through `RawTaskSchema`, so a
 * `javascript:` link admitted at a door committed a `raw_tasks` row and threw
 * afterwards — an ask filed, never nudged, never audited, and unfilable again under
 * the same dedupe key. One schema at every door, so a third door cannot re-hand-roll
 * the bound and re-open it.
 */
export const HttpUrlSchema = z
  .string()
  .min(1)
  .max(RAW_TASK_URL_MAX)
  .refine(isHttpUrl, 'expected an http(s) URL');

/**
 * Does this parse as an `http(s)` URL? Exported because the intake projection
 * FILTERS on it (a junk `source_ref` is simply not cited) while the schema
 * REFUSES on it (a hand-built draft carrying one is a bug) — two postures, one
 * predicate, so they can never disagree about what a link is.
 */
export function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * One `path:line` anchor. `line` is optional because a file-level anchor is a
 * real citation ("this module"), and absent is not the same as line 0.
 */
export const RawTaskAnchorSchema = z.object({
  path: z.string().min(1).max(RAW_TASK_ANCHOR_PATH_MAX),
  line: z.number().int().positive().optional(),
});
export type RawTaskAnchor = z.infer<typeof RawTaskAnchorSchema>;

/**
 * One path the filer expects the work to WRITE (#5287) — read off a checkout, by the scout
 * or the planner that held it. Evidence, never a lock: triage still declares the AREAS each
 * piece works in, and a write only narrows a declared area to the files beneath it
 * (`triageFileSet`, `@developerz/agent`). `why` is the filer's one line on it.
 */
export const RawTaskWriteSchema = z.object({
  path: z.string().min(1).max(RAW_TASK_ANCHOR_PATH_MAX),
  why: z.string().min(1).max(RAW_TASK_WRITE_WHY_MAX).optional(),
});
export type RawTaskWrite = z.infer<typeof RawTaskWriteSchema>;

/**
 * What makes the ask checkable. The three citation lists default to empty: a raw task
 * with no evidence is a weak raw task, not a malformed one, and refusing it
 * would push inlets into inventing citations.
 *
 * `writes` is OPTIONAL rather than defaulted, and absent means what it says: the filer
 * stated no write set. Anchors are what to READ; writes are what the filer saw the change
 * needs to WRITE. They are kept apart because a read citation widened into a lock is a
 * measured incident (`evidence-paths.ts`, `@developerz/agent`).
 */
export const RawTaskEvidenceSchema = z.object({
  anchors: z.array(RawTaskAnchorSchema).max(RAW_TASK_ANCHORS_MAX).default([]),
  urls: z.array(HttpUrlSchema).max(RAW_TASK_URLS_MAX).default([]),
  excerpts: z
    .array(z.string().min(1).max(RAW_TASK_EXCERPT_MAX))
    .max(RAW_TASK_EXCERPTS_MAX)
    .default([]),
  writes: z.array(RawTaskWriteSchema).max(RAW_TASK_WRITES_MAX).optional(),
  /**
   * OPTIONAL, absent meaning the filer named no metric — only a scheduled checkup's
   * filing carries one (#5376). Rendered to triage as evidence (`renderAsk`).
   */
  metric: z.string().min(1).max(RAW_TASK_METRIC_MAX).optional(),
  /**
   * The ask's text holds a run's or a judge's prose (`apps/worker` `cron/ask-close-loop*.ts`, the
   * re-file note and the diagnosis fix), which may quote outsiders. So the intake screen reads it
   * even when a developer filed it (#7785): WHO wrote the ask and WHAT its text is are two facts.
   * OPTIONAL, and this object is not strict, so an older pod reads it. It can only add a screen.
   */
  text_from_run: z.literal(true).optional(),
});
export type RawTaskEvidence = z.infer<typeof RawTaskEvidenceSchema>;

/**
 * The thing that filed this — the issue, the Sentry group, the scout run. Two
 * fields and no more: `kind` names the namespace, `id` is that namespace's
 * stable id for the THING (never for the delivery, or a webhook that fires on
 * every edit files a raw task per comment).
 */
export const RawTaskOriginRefSchema = z.object({
  kind: z.string().min(1).max(40),
  id: z.string().min(1).max(200),
});
export type RawTaskOriginRef = z.infer<typeof RawTaskOriginRefSchema>;

/** Cap on each string of an {@link ErrorSignature}. The normalizer CLAMPS untrusted text to it. */
export const ERROR_SIGNATURE_FIELD_MAX = 200;

const ErrorSignatureTextSchema = z.string().min(1).max(ERROR_SIGNATURE_FIELD_MAX);

/**
 * What an error delivery may show about itself, as DATA (`raw_tasks.error_signature`,
 * #6223): the exception CLASS, the crash-site frame's file and function, and the
 * vendor's group id. Never the error MESSAGE: it rides the title and may carry user
 * data, so no field here is ever read from a message or from Sentry's
 * `metadata.value`. `group_id` is the envelope's `source_ref`, the same ref that keys
 * the ask's dedupe key.
 */
export const ErrorSignatureSchema = z.object({
  exception_class: ErrorSignatureTextSchema.nullable(),
  file: ErrorSignatureTextSchema.nullable(),
  function: ErrorSignatureTextSchema.nullable(),
  group_id: ErrorSignatureTextSchema,
  /**
   * OPTIONAL (#5653): whether the stored crash frame was `in_app`. Absent on a
   * row written before this field reads as NOT in-app; the metadata fallback
   * (no event, no frame) stores `false`.
   */
  frame_in_app: z.boolean().optional(),
});
export type ErrorSignature = z.infer<typeof ErrorSignatureSchema>;
